import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_API_URL } from "utils/constants";

const performanceConfig = {
  totalCoursesCompleted: {
    heading: "Total Trainings Completed",
    image: "/Assets/HallFame/performance-icon1.png",
  },
  totalBadgesEarned: {
    heading: "Badges Earned",
    image: "/Assets/HallFame/performance-icon2.png",
  },
  totalHours: {
    heading: "Total Hours",
    image: "/Assets/HallFame/performance-icon3.png",
  },
  completionPercentage: {
    heading: "Completion Status",
    image: "/Assets/HallFame/performance-icon2.png",
  },
};

const getExtrasByRank = (index) => {
  const map = [
    {
      medal: "/Assets/HallFame/platinum-medal.png",
      ranking: "/Assets/HallFame/rankimg2.png",
    },
    {
      medal: "/Assets/HallFame/diamond-medal.png",
      ranking: "/Assets/HallFame/rankimg1.png",
    },
    {
      medal: "/Assets/HallFame/gold-medal.png",
      ranking: "/Assets/HallFame/rankimg3.png",
    },
  ];

  return map[index] ?? { medal: null, ranking: null };
};

const safeFetchJson = async (url, options) => {
  const res = await fetch(url, options);

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Request failed: ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
    );
  }

  return res.json();
};

export const fetchHallOfFameData = createAsyncThunk(
  "hallOfFame/fetchHallOfFameData",
  async (businessUnit, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return rejectWithValue("Authorization token not found");
      }

      const headers = {
        Authorization: token,
        "Content-Type": "application/json",
      };

      const bu = encodeURIComponent(businessUnit || "Song");

      const [hallResponse, performerDataRaw] = await Promise.all([
        safeFetchJson(`${BASE_API_URL}/overallPerformance?businessUnit=${bu}`, {
          headers,
        }),
        safeFetchJson(`${BASE_API_URL}/getTopLearners?businessUnit=${bu}`, {
          headers,
        }),
      ]);

      const hallData = Array.isArray(hallResponse)
        ? typeof hallResponse[0] === "object"
          ? hallResponse[0]
          : {}
        : typeof hallResponse === "object"
          ? hallResponse
          : {};

      const hallFameData = Object.entries(hallData)
        .filter(([key]) => performanceConfig[key])
        .map(([key, value]) => ({
          id: key,
          heading: performanceConfig[key].heading,
          number:
            key === "completionPercentage" ? `${value ?? 0}%` : (value ?? 0),
          image: performanceConfig[key].image,
        }));

      const cleanData = (
        Array.isArray(performerDataRaw) ? performerDataRaw : []
      )
        .filter((x) => x?.enterpriseId)
        .reduce((acc, curr) => {
          if (!acc.some((x) => x.enterpriseId === curr.enterpriseId)) {
            acc.push({
              ...curr,
              totalPoints: Number(curr.totalPoints || 0),
            });
          }
          return acc;
        }, []);

      const top3 = cleanData
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .slice(0, 3);

      const hallFamePerformersData = [top3[1], top3[0], top3[2]]
        .filter(Boolean)
        .map((item, index) => {
          const extras = getExtrasByRank(index);
          return {
            ...item,
            medal: extras.medal ?? "/Assets/HallFame/gold-medal.png",
            ranking: extras.ranking ?? "/Assets/HallFame/rankimg3.png",
          };
        });

      const enterpriseIds = hallFamePerformersData
        .map((x) => x?.enterpriseId)
        .filter(Boolean);

      return {
        hallFameData,
        hallFamePerformersData,
        enterpriseIds,
      };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load data");
    }
  },
);

const hallOfFameSlice = createSlice({
  name: "hallOfFame",
  initialState: {
    hallFameData: [],
    hallFamePerformersData: [],
    enterpriseIds: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearHallOfFameData: (state) => {
      state.hallFameData = [];
      state.hallFamePerformersData = [];
      state.enterpriseIds = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHallOfFameData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHallOfFameData.fulfilled, (state, action) => {
        state.loading = false;
        state.hallFameData = action.payload.hallFameData;
        state.hallFamePerformersData = action.payload.hallFamePerformersData;
        state.enterpriseIds = action.payload.enterpriseIds;
      })
      .addCase(fetchHallOfFameData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load data";
        state.hallFameData = [];
        state.hallFamePerformersData = [];
        state.enterpriseIds = [];
      });
  },
});

export const { clearHallOfFameData } = hallOfFameSlice.actions;
export default hallOfFameSlice.reducer;
